import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import {
  fetchYowanPublicBlogDetail,
  fetchYowanPublicBlogList,
  YOWAN_CONTENT_PUBLIC_PAGE_SIZE,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const BLOG_EXCERPT_MAX_CHARS = 120;
const BLOG_PAGE_SIZE = YOWAN_CONTENT_PUBLIC_PAGE_SIZE;
const YOWAN_PREVIEW_SEARCH = '?domain=yowan';

const isLocalPreviewHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const getBlogPreviewSearch = (search = '') => {
  if (search.includes('domain=yowan')) {
    return search;
  }

  return isLocalPreviewHost() ? YOWAN_PREVIEW_SEARCH : search;
};

const appendBlogPreviewQuery = (target, currentSearch = '') => {
  if (!currentSearch.includes('domain=yowan')) {
    return target;
  }

  const searchIndex = target.indexOf('?');
  const pathname = searchIndex >= 0 ? target.slice(0, searchIndex) : target;
  const targetSearch = searchIndex >= 0 ? target.slice(searchIndex + 1) : '';
  const searchParams = new URLSearchParams(targetSearch);

  searchParams.set('domain', 'yowan');

  return `${pathname}?${searchParams.toString()}`;
};

const truncateExcerpt = (text) => {
  if (!text || text.length <= BLOG_EXCERPT_MAX_CHARS) {
    return { excerpt: text, truncated: false };
  }

  return {
    excerpt: `${text.slice(0, BLOG_EXCERPT_MAX_CHARS).trimEnd()}`,
    truncated: true,
  };
};

const getBlogEntryPreviewText = (entry = {}) => entry.excerpt || entry.bodyPreview || '';

const getBlogAuthorName = (author = null) => {
  if (!author) {
    return '';
  }

  if (typeof author === 'string') {
    return author;
  }

  return author.displayName || author.display_name || author.name || '';
};

const getBlogAuthorUrl = (author = null) => {
  if (!author || typeof author !== 'object') {
    return '';
  }

  return author.profileUrl || author.profile_url || '';
};

const BlogAuthorAttribution = ({ author, className }) => {
  const name = getBlogAuthorName(author);
  const url = getBlogAuthorUrl(author);

  if (!name) {
    return null;
  }

  return (
    <p className={className}>
      <span className="yowan-blog-meta__label">author: </span>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {name}
        </a>
      ) : (
        name
      )}
    </p>
  );
};

const buildBodyPreview = (body = []) => {
  if (Array.isArray(body)) {
    return body.join('\n\n').trim();
  }

  return String(body || '').trim();
};

const BlogPage = () => {
  const content = yowanPageContent['/blog'];
  const metadata = getYowanRouteMeta('/blog');
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const totalPages = Math.max(1, Math.ceil(totalCount / BLOG_PAGE_SIZE));
  const currentPage =
    Number.isNaN(requestedPage) || requestedPage < 1
      ? 1
      : requestedPage;
  const pageEntries = entries;
  const previewSearch = getBlogPreviewSearch(location.search);

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanPublicBlogList({ page: currentPage, pageSize: BLOG_PAGE_SIZE })
      .then(async (payload) => {
        const nextEntries = payload.results;

        if (isMounted) {
          setEntries(nextEntries);
          setTotalCount(payload.count);
        }

        const entriesMissingExcerpt = nextEntries.filter((entry) => !entry.excerpt && entry.slug);

        if (!entriesMissingExcerpt.length) {
          return;
        }

        const bodyPreviewEntries = await Promise.all(
          entriesMissingExcerpt.map((entry) =>
            fetchYowanPublicBlogDetail(entry.slug)
              .then((detail) => [entry.slug, buildBodyPreview(detail.body)])
              .catch(() => [entry.slug, '']),
          ),
        );

        if (isMounted) {
          const bodyPreviewMap = new Map(bodyPreviewEntries);
          setEntries((currentEntries) =>
            currentEntries.map((entry) => ({
              ...entry,
              bodyPreview: entry.excerpt ? entry.bodyPreview : bodyPreviewMap.get(entry.slug) || entry.bodyPreview || '',
            })),
          );
        }
      })
      .catch(() => {
        if (isMounted) {
          setEntries([]);
          setTotalCount(0);
          setLoadError('記事を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentPage]);

  const changePage = (page) => {
    const nextParams = new URLSearchParams(searchParams);

    if (page <= 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(page));
    }

    if (isLocalPreviewHost()) {
      nextParams.set('domain', 'yowan');
    }

    setSearchParams(nextParams);
  };

  return (
    <>
      <Section>
        <PageContainer>
          <div className="yowan-blog-header">
            <div className="yowan-blog-header__body">
              <Heading as="h1">{content.header.title}</Heading>
              {content.header.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </PageContainer>
      </Section>
      <Section>
        <PageContainer>
          <div className="yowan-blog-list">
            {pageEntries.map((entry) => {
              const { excerpt, truncated } = truncateExcerpt(getBlogEntryPreviewText(entry));
              const hasEntryImage =
                Boolean(entry.primary_image?.thumbnail_url || entry.primary_image?.url || entry.imageLabel);

              return (
                <article
                  key={`${entry.date}-${entry.title}`}
                  className={`yowan-blog-entry${hasEntryImage ? '' : ' yowan-blog-entry--no-image'}`}
                >
                  {entry.primary_image?.thumbnail_url || entry.primary_image?.url ? (
                    <div className="yowan-blog-entry__image">
                      <img
                        src={entry.primary_image.thumbnail_url || entry.primary_image.url}
                        alt={entry.primary_image.alt_text || entry.title}
                      />
                    </div>
                  ) : entry.imageLabel ? (
                    <div className="yowan-blog-entry__image" aria-hidden="true">
                      <span>{entry.imageLabel}</span>
                    </div>
                  ) : null}
                  <div className="yowan-blog-entry__content">
                    <div className="yowan-blog-entry__meta">
                      <BlogAuthorAttribution author={entry.author} className="yowan-blog-entry__author" />
                      <p className="yowan-blog-entry__date">
                        <span className="yowan-blog-meta__label">date: </span>
                        <span>{entry.date}</span>
                      </p>
                    </div>
                    <Link
                      className="yowan-blog-entry__title"
                      to={appendBlogPreviewQuery(entry.href, previewSearch)}
                    >
                      {entry.title}
                    </Link>
                    <Link
                      className="yowan-blog-entry__excerpt-link"
                      to={appendBlogPreviewQuery(entry.href, previewSearch)}
                    >
                      <p className="yowan-blog-entry__excerpt">
                        {excerpt}
                        {truncated ? (
                          <span className="yowan-blog-entry__read-more">...read more</span>
                        ) : null}
                      </p>
                    </Link>
                  </div>
                </article>
              );
            })}
            {!pageEntries.length ? (
              <p className="yowan-admin-form__message">
                {isLoading ? <LoadingSpinner /> : loadError || '表示できる記事がありません。'}
              </p>
            ) : null}
          </div>
          {totalPages > 1 ? (
            <nav className="yowan-blog-pagination" aria-label="ブログ一覧のページ送り">
              <button
                className="yowan-blog-pagination__link"
                type="button"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                前へ
              </button>
              <div className="yowan-blog-pagination__numbers">
                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  const isActive = page === currentPage;

                  return (
                    <button
                      key={page}
                      className={`yowan-blog-pagination__link${isActive ? ' is-active' : ''}`}
                      type="button"
                      onClick={() => changePage(page)}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              <button
                className="yowan-blog-pagination__link"
                type="button"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                次へ
              </button>
            </nav>
          ) : null}
        </PageContainer>
      </Section>
    </>
  );
};

export default BlogPage;
